import { isMouseEvent } from "../../../base/browser/dom";
import { IMouseEvent, StandardMouseEvent } from "../../../base/browser/mouseEvent";
import { Emitter } from "../../../base/common/events";
import { Disposable, DisposableStore, IDisposable, toDisposable } from "../../../base/common/lifecycle";
import { IWindowsMainService } from "../../windows/common/windows";
import { IPointerEvent, IPointerService, IPointerWheelEvent, PointerListener, PointerListenerOptions, PointerWheelListener } from "../common/pointer";


type PointerEventType = "pointerdown" | "dblclick" | "pointermove" | "pointerup" | "pointercancel" | "contextmenu" | "wheel";
const POINTER_EVENTS: readonly PointerEventType[] = ["pointerdown", "dblclick", "pointermove", "pointerup", "pointercancel", "contextmenu", "wheel"];


export class BrowserPointerService extends Disposable implements IPointerService {
    _serviceBrand: undefined;

    // One emitter per (event type, phase). Consumers subscribe to these; the
    // service re-wires which windows feed them as windows open/close, so a
    // subscription survives popouts without the consumer ever touching `window`.
    // Capture and bubble are separate so `stopPropagation` on the way up can be
    // deliberately respected (bubble) or bypassed (capture — the default).
    private readonly emitters = new Map<PointerEventType, { capture: Emitter<IPointerEvent>; bubble: Emitter<IPointerEvent> }>();

    /** Listeners per window — ADDITIVE: every open window is listened to at once,
     *  so input from a popout drives the same subscriptions as the main window
     *  (the previous last-window-wins behavior went input-dead on the main window
     *  the moment a popout opened). */
    private readonly attachedWindows = new Map<Window, DisposableStore>();

    /** Live `MouseEvent.buttons` bitmask across ALL windows (see
     *  {@link IPointerService.isButtonPressed}) — updated by the capture-phase
     *  listeners, so it's current even when no consumer subscribed. */
    private _buttons = 0;

    constructor(
        @IWindowsMainService windowsMainService: IWindowsMainService
    ) {
        super();

        for (const type of POINTER_EVENTS) {
            const pair = { capture: new Emitter<IPointerEvent>(), bubble: new Emitter<IPointerEvent>() };
            this._register(pair.capture);
            this._register(pair.bubble);
            this.emitters.set(type, pair);
        }

        this._register(windowsMainService.onDidOpenWindow((evt) => this.attach(evt.getWindow())));
        this._register(windowsMainService.onDidDestroyWindow((evt) => this.detach(evt.getWindow())));

        this.attach(globalThis.window);
    }

    isButtonPressed(buttonsMask: number): boolean {
        return (this._buttons & buttonsMask) !== 0;
    }

    onMouseDown(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("pointerdown", listener, options);
    }
    onDoubleClick(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("dblclick", listener, options);
    }
    onMouseMove(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("pointermove", listener, options);
    }
    onMouseUp(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("pointerup", listener, options);
    }
    onMouseCancel(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("pointercancel", listener, options);
    }
    onContextMenu(listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("contextmenu", listener, options);
    }

    onWheel(listener: PointerWheelListener, options?: PointerListenerOptions): IDisposable {
        return this.subscribe("wheel", event => listener(event as IPointerWheelEvent), options);
    }

    private subscribe(type: PointerEventType, listener: PointerListener, options?: PointerListenerOptions): IDisposable {
        const capture = options?.capture !== false; // capture is the robust default
        // Pinned to one window: a dedicated listener, not the shared fan-out.
        if (options?.window) {
            return this.listenOn(options.window, type, capture, listener);
        }
        return this.emitters.get(type)![capture ? "capture" : "bubble"].event(listener);
    }

    /** Tag an event with the window it was observed in (see {@link IPointerEvent}).
     *  The listener is attached per window, so the source is known here — consumers
     *  should never have to dig it back out of the DOM. */
    private toPointerEvent(window: Window, event: MouseEvent): IPointerEvent {
        return Object.assign(new StandardMouseEvent(window, event), { sourceWindow: window });
    }

    /** A dedicated listener on a single window (for {@link PointerListenerOptions.window}). */
    private listenOn(window: Window, type: PointerEventType, capture: boolean, listener: PointerListener): IDisposable {
        const handler = (event: Event) => {
            if (isMouseEvent(event)) listener(this.toPointerEvent(window, event));
        };
        window.addEventListener(type, handler, { capture, ...(type === "wheel" ? { passive: false } : {}) });
        return toDisposable(() => window.removeEventListener(type, handler, { capture }));
    }

    private attach(window: Window): void {
        if (this.attachedWindows.has(window)) return;
        const store = new DisposableStore();
        this.attachedWindows.set(window, store);
        this._register(store);

        for (const type of POINTER_EVENTS) {
            const pair = this.emitters.get(type)!;
            for (const capture of [true, false] as const) {
                const emitter = capture ? pair.capture : pair.bubble;
                const handler = (event: Event) => {
                    // Button STATE tracks every event (before the early return —
                    // it must stay current with zero subscribers).
                    if (capture && type !== "wheel" && isMouseEvent(event)) this._buttons = event.buttons ?? 0;
                    // Skip the StandardMouseEvent allocation when nobody subscribed
                    // to this phase (e.g. high-frequency pointermove, bubble unused).
                    if (!emitter.hasListeners()) return;
                    if (isMouseEvent(event)) emitter.fire(this.toPointerEvent(window, event));
                };
                window.addEventListener(type, handler, { capture, ...(type === "wheel" ? { passive: false } : {}) });
                store.add(toDisposable(() => window.removeEventListener(type, handler, { capture })));
            }
        }
    }

    private detach(window: Window): void {
        const store = this.attachedWindows.get(window);
        if (!store) return;
        this.attachedWindows.delete(window);
        store.dispose();
    }
}
